/**
 * Legacy mapper - converts InvoiceData to XRechnungInvoiceData
 */

import {InvoiceData, InvoiceEmitterInfo, InvoiceLineItem} from '../invoiceGenerator';
import {
    XRechnungInvoiceData,
    XRechnungLineItem,
    BuyerParty,
    SellerParty,
    PaymentMeans,
    VatBreakdown,
    XRechnungOptions,
} from './types';
import {BOEHRINGER_SELLER, BOEHRINGER_PAYMENT_MEANS, XRECHNUNG_SPEC_ID, DEFAULT_PAYMENT_DAYS} from './sellerConstants';
import {mapUnitToCode} from './unitCodes';

/**
 * Parse address string into components
 * Tries to extract street and city/zip from simple address formats
 */
function parseAddress(street: string, zipCity: string): {streetName: string; postalZone: string; cityName: string} {
    // Parse zipCity (e.g., "74336 Brackenheim" or "74072 Heilbronn")
    const zipCityMatch = zipCity.match(/^(\d{5})\s+(.+)$/);

    if (zipCityMatch) {
        return {
            streetName: street,
            postalZone: zipCityMatch[1],
            cityName: zipCityMatch[2],
        };
    }

    // Fallback if format doesn't match
    return {
        streetName: street,
        postalZone: '00000',
        cityName: zipCity,
    };
}

/**
 * Convert legacy InvoiceLineItem to XRechnungLineItem
 */
function mapLineItem(item: InvoiceLineItem, index: number, vatRate: number): XRechnungLineItem {
    // Determine unit price - if not provided, use total (for materials, lump sums)
    const unitPrice = item.unitPrice ?? (item.quantity > 0 ? item.total / item.quantity : item.total);

    return {
        id: String(index + 1),
        name: item.description,        // Use description as item name (BT-153)
        note: item.note,               // Use note as optional description (BT-154)
        quantity: item.quantity,
        unitCode: mapUnitToCode(item.unit),
        unitPrice: unitPrice,
        lineNetAmount: item.total,
        vatCategoryCode: 'S', // Standard rate
        vatRate: vatRate,
    };
}

/**
 * Round to 2 decimal places to avoid floating point precision issues
 */
function round2(value: number): number {
    return Math.round(value * 100) / 100;
}

/**
 * Calculate VAT breakdown from line items
 */
function calculateVatBreakdown(lineItems: XRechnungLineItem[]): VatBreakdown[] {
    // Group by VAT rate
    const byRate = new Map<number, {taxable: number; categoryCode: string}>();

    for (const item of lineItems) {
        const existing = byRate.get(item.vatRate) || {taxable: 0, categoryCode: item.vatCategoryCode};
        existing.taxable += item.lineNetAmount;
        byRate.set(item.vatRate, existing);
    }

    // Convert to VatBreakdown array - round all amounts to 2 decimal places
    return Array.from(byRate.entries()).map(([rate, data]) => ({
        categoryCode: data.categoryCode,
        rate: rate,
        taxableAmount: round2(data.taxable),
        taxAmount: round2(data.taxable * (rate / 100)),
    }));
}

/**
 * Calculate due date from invoice date
 */
function calculateDueDate(invoiceDate: Date, days: number): Date {
    const dueDate = new Date(invoiceDate);
    dueDate.setDate(dueDate.getDate() + days);
    return dueDate;
}

/**
 * Helper to get non-empty string or undefined
 */
function nonEmpty(value?: string): string | undefined {
    return value && value.trim() ? value.trim() : undefined;
}

/**
 * Create seller party from emitter data or fall back to constants
 * IMPORTANT: BR-CO-26 requires at least one of: VAT ID (BT-31), Company ID (BT-30), or Party ID (BT-29)
 * Note: Tax number (BT-32) does NOT satisfy BR-CO-26!
 */
function createSellerParty(emitter?: InvoiceEmitterInfo): SellerParty {
    if (!emitter) {
        return BOEHRINGER_SELLER;
    }

    // Get emitter values
    const emitterVatId = nonEmpty(emitter.vat_id);
    const emitterTaxNumber = nonEmpty(emitter.tax_number);
    const emitterCompanyId = nonEmpty(emitter.company_id);

    // Check if VAT ID has valid country prefix (required for BT-31)
    const hasValidVatId = emitterVatId && /^[A-Z]{2}/.test(emitterVatId);

    // BR-CO-26 requires BT-29 (PartyIdentification), BT-30 (CompanyID), or BT-31 (VAT ID with prefix)
    // Tax number (BT-32) does NOT satisfy BR-CO-26!
    // Always ensure companyId is present as fallback for BR-CO-26 compliance
    const companyId = emitterCompanyId || BOEHRINGER_SELLER.companyId;

    // If no valid VAT ID and no company ID from emitter, also use fallback VAT ID
    const vatId = hasValidVatId ? emitterVatId : (emitterCompanyId ? undefined : BOEHRINGER_SELLER.identification.vatId);

    return {
        name: nonEmpty(emitter.name) || BOEHRINGER_SELLER.name,
        registrationName: nonEmpty(emitter.name) || BOEHRINGER_SELLER.registrationName,
        identification: {
            vatId: vatId,
            taxNumber: emitterTaxNumber || BOEHRINGER_SELLER.identification.taxNumber,
        },
        postalAddress: {
            streetName: nonEmpty(emitter.street) || BOEHRINGER_SELLER.postalAddress.streetName,
            cityName: nonEmpty(emitter.city) || BOEHRINGER_SELLER.postalAddress.cityName,
            postalZone: nonEmpty(emitter.postal_code) || BOEHRINGER_SELLER.postalAddress.postalZone,
            countryCode: nonEmpty(emitter.country_code) || 'DE',
        },
        contact: {
            telephone: nonEmpty(emitter.contact_phone) || BOEHRINGER_SELLER.contact?.telephone,
            email: nonEmpty(emitter.contact_email) || BOEHRINGER_SELLER.contact?.email,
        },
        // ALWAYS have companyId for BR-CO-26 (BT-30) compliance
        companyId: companyId,
    };
}

/**
 * Create payment means from emitter data or fall back to constants
 */
function createPaymentMeans(emitter?: InvoiceEmitterInfo, paymentReference?: string): PaymentMeans {
    if (!emitter) {
        return {
            ...BOEHRINGER_PAYMENT_MEANS,
            paymentReference,
        };
    }

    return {
        paymentMeansCode: '58', // SEPA credit transfer
        iban: emitter.iban,
        bic: emitter.bic,
        accountName: emitter.name,
        financialInstitutionName: emitter.bank_name,
        paymentReference,
    };
}

/**
 * Create buyer party from legacy customer data
 */
function createBuyerParty(
    customer: InvoiceData['customer'],
    options?: XRechnungOptions
): BuyerParty {
    const {streetName, postalZone, cityName} = parseAddress(customer.street, customer.zipCity);

    const buyer: BuyerParty = {
        name: customer.name,
        postalAddress: {
            streetName,
            postalZone,
            cityName,
            countryCode: 'DE',
        },
    };

    // Merge with additional buyer data from options
    if (options?.buyerData) {
        if (options.buyerData.identification) {
            buyer.identification = {
                ...buyer.identification,
                ...options.buyerData.identification,
            };
        }
        if (options.buyerData.contact) {
            buyer.contact = options.buyerData.contact;
        }
        if (options.buyerData.postalAddress) {
            buyer.postalAddress = {
                ...buyer.postalAddress,
                ...options.buyerData.postalAddress,
            };
        }
    }

    // Add buyer reference
    if (options?.buyerReference) {
        buyer.buyerReference = options.buyerReference;
    }

    return buyer;
}

/**
 * Map legacy InvoiceData to XRechnungInvoiceData
 */
export function mapLegacyInvoiceData(
    data: InvoiceData,
    options?: XRechnungOptions
): XRechnungInvoiceData {
    // Map line items
    const lineItems = data.lineItems.map((item, index) =>
        mapLineItem(item, index, data.vatRate)
    );

    // Calculate totals - round all amounts to ensure BR-CO-15 compliance
    // BR-CO-15: Invoice total with VAT (BT-112) = Invoice total without VAT (BT-109) + Invoice total VAT (BT-110)
    const lineExtensionAmount = round2(lineItems.reduce((sum, item) => sum + item.lineNetAmount, 0));
    const vatBreakdown = calculateVatBreakdown(lineItems);
    const totalTax = round2(vatBreakdown.reduce((sum, vat) => sum + vat.taxAmount, 0));
    const taxExclusiveAmount = lineExtensionAmount;
    const taxInclusiveAmount = round2(taxExclusiveAmount + totalTax);

    // Calculate due date
    const dueDate = calculateDueDate(data.invoiceDate, DEFAULT_PAYMENT_DAYS);

    // Create buyer party
    const buyer = createBuyerParty(data.customer, options);

    // Create seller party from emitter data or use defaults
    const seller = createSellerParty(data.emitter);

    // Create payment means from emitter data or use defaults
    const paymentMeans = createPaymentMeans(data.emitter, data.invoiceNumber);

    return {
        // Document identification
        invoiceNumber: data.invoiceNumber,
        invoiceDate: data.invoiceDate,
        invoiceTypeCode: '380', // Commercial invoice
        currencyCode: 'EUR',
        buyerReference: options?.buyerReference || data.invoiceNumber,

        // Process control
        specificationId: XRECHNUNG_SPEC_ID,

        // Note with work description
        note: data.durchgefuehrteArbeiten
            ? `Objekt: ${data.objekt}. ${data.durchgefuehrteArbeiten}`
            : undefined,

        // Parties
        seller,
        buyer,

        // Delivery - use buyer address as delivery address for service invoices
        deliveryDate: data.durchgefuehrtAm,
        deliveryLocation: data.objekt,
        deliveryAddress: {
            streetName: data.objekt || buyer.postalAddress.streetName,
            cityName: buyer.postalAddress.cityName,
            postalZone: buyer.postalAddress.postalZone,
            countryCode: buyer.postalAddress.countryCode,
        },

        // Payment
        paymentMeans,
        paymentTerms: {
            dueDate,
            dueDays: DEFAULT_PAYMENT_DAYS,
            note: `Zahlbar innerhalb von ${DEFAULT_PAYMENT_DAYS} Tagen ab Rechnungsstellung.`,
        },

        // Line items
        lineItems,

        // Totals
        lineExtensionAmount,
        taxExclusiveAmount,
        taxInclusiveAmount,
        payableAmount: taxInclusiveAmount,

        // VAT breakdown
        vatBreakdown,

        // Service-specific
        objekt: data.objekt,
        durchgefuehrteArbeiten: data.durchgefuehrteArbeiten,
        durchgefuehrtAm: data.durchgefuehrtAm,
    };
}
